import React, { FC, ReactElement, ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DeleteSensorModal from './modals/DeleteSensorModal';
import AddSensorModal from './modals/AddSensorModal';

import { Typography, IconButton, Tooltip } from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSelector } from 'react-redux';
import { ReduxState } from '../../../../reducers';
import {
  DeviceGroupDto,
  DeviceSensorDto,
  Device,
  MutateDeviceSensor,
  PortsConfig,
} from '@thingslog/repositories';
import { DataGrid, GridColDef, GridRenderCellParams } from '@thingslog/ui-components';

interface SensorTableRow {
  id: string;
  deviceNumber: string;
  deviceLabel: string;
  sensorIndex: number;
  sensorName: string;
}

//Component
const SensorsTable: FC<SensorsTableProps> = ({
  selectedDeviceGroup,
  selectedDeviceNumber,
  onSelectedDeviceChange,
  selectedDevicePorts,
  isOpenedDeleteSensorModal,
  isOpenedAddSensorModal,
  onAddSensorModalStateChange,
  onDeleteSensorModalStateChange,
  addDeviceSensorToGroup,
  deleteDeviceAndSensorFromGroup,
}: SensorsTableProps) => {
  const { t } = useTranslation();

  const devices = useSelector((state: ReduxState) => state.dev.devicesArray);
  const deviceLabelsByNumber = useMemo(
    (): Map<string, string> =>
      new Map(
        devices.map((device: Device) => [
          String(device.number),
          device.name ? `${device.name} (${device.number})` : String(device.number),
        ])
      ),
    [devices]
  );

  const [sensorsByGroupPageSize, setSensorsByGroupPageSize] = useState<number>(15);
  const [selectedSensor, setSelectedSensor] = useState<DeviceSensorDto>({
    deviceNumber: '',
    sensorIndex: 0,
    sensorName: '',
  });

  const sensorRows = useMemo(
    (): SensorTableRow[] =>
      (selectedDeviceGroup?.deviceSensors || []).map((sensorDeviceGroup: DeviceSensorDto) => {
        const deviceLabel: string =
          deviceLabelsByNumber.get(sensorDeviceGroup.deviceNumber) ??
          String(sensorDeviceGroup.deviceNumber);

        return {
          id: `${sensorDeviceGroup.deviceNumber}-${sensorDeviceGroup.sensorIndex}`,
          deviceNumber: sensorDeviceGroup.deviceNumber,
          deviceLabel,
          sensorIndex: sensorDeviceGroup.sensorIndex,
          sensorName: sensorDeviceGroup.sensorName,
        };
      }),
    [deviceLabelsByNumber, selectedDeviceGroup]
  );

  const columns = useMemo(
    (): GridColDef[] => [
      {
        field: 'deviceLabel',
        headerName: t('device_groups_sensor_table_device_number_header'),
        flex: 1.2,
        minWidth: 180,
      },
      {
        field: 'sensorName',
        headerName: t('device_groups_sensor_table_sensor_name_header'),
        flex: 1.2,
        minWidth: 180,
      },
      {
        field: 'sensorIndex',
        headerName: t('device_groups_sensor_table_sensor_index_header'),
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        flex: 0.6,
        minWidth: 120,
      },
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        filterable: false,
        align: 'right',
        width: 100,
        renderHeader: (): ReactNode =>
          selectedDeviceGroup ? (
            <Tooltip title={<span>{t('device_groups_btn_add')}</span>} arrow placement="top">
              <IconButton
                aria-label="Add"
                size="large"
                onClick={(): void => onAddSensorModalStateChange(true)}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <></>
          ),
        renderCell: (params: GridRenderCellParams<SensorTableRow>): ReactElement => (
          <Tooltip title={<span>{t('device_groups_btn_delete')}</span>} arrow placement="top">
            <IconButton
              aria-label="Delete"
              size="large"
              onClick={(event: React.MouseEvent<HTMLButtonElement>): void => {
                event.stopPropagation();
                onDeleteSensorModalStateChange(true);
                setSelectedSensor({
                  deviceNumber: params.row.deviceNumber,
                  sensorIndex: params.row.sensorIndex,
                  sensorName: params.row.sensorName,
                });
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [onAddSensorModalStateChange, onDeleteSensorModalStateChange, selectedDeviceGroup, t]
  );

  return (
    <>
      <div className="ml-5" style={{ width: '100%' }}>
        <DataGrid
          autoHeight
          density="compact"
          disableColumnMenu
          disableSelectionOnClick
          hideFooterSelectedRowCount
          pageSize={sensorsByGroupPageSize}
          rowsPerPageOptions={[10, 15, 25, 50, 100]}
          onPageSizeChange={(pageSize: number): void => setSensorsByGroupPageSize(pageSize)}
          rows={sensorRows}
          columns={columns}
        />
        {selectedDeviceGroup === null && (
          <Typography className="my-20" variant="h5">
            {t('device_groups_sensor_table_select_message')}
          </Typography>
        )}
      </div>
      <DeleteSensorModal
        selectedDeviceGroupId={selectedDeviceGroup?.deviceGroupId || null}
        isOpenedDeleteSensorModal={isOpenedDeleteSensorModal}
        selectedSensor={selectedSensor}
        handleOnCloseModal={(): void => onDeleteSensorModalStateChange(false)}
        handleOnDeleteDeviceAndSensorFromGroup={deleteDeviceAndSensorFromGroup}
      />
      <AddSensorModal
        isOpenedAddSensorModal={isOpenedAddSensorModal}
        selectedDeviceGroupId={selectedDeviceGroup?.deviceGroupId || null}
        devices={devices}
        selectedDeviceNumber={selectedDeviceNumber}
        onSelectedDeviceChange={onSelectedDeviceChange}
        selectedDevicePorts={selectedDevicePorts}
        handleOnCloseModal={(): void => onAddSensorModalStateChange(false)}
        handleOnCreateSensorDeviceToGroup={addDeviceSensorToGroup}
      />
    </>
  );
};

interface SensorsTableProps {
  companyId: number;
  selectedDeviceNumber: string | null;
  onSelectedDeviceChange: (deviceNumber: string | null) => void;
  selectedDeviceGroup: DeviceGroupDto | null;
  selectedDevicePorts: PortsConfig;
  isOpenedAddSensorModal: boolean;
  onAddSensorModalStateChange: (isOpen: boolean) => void;
  addDeviceSensorToGroup: (deviceGroupId: number, body: MutateDeviceSensor) => void;
  isOpenedDeleteSensorModal: boolean;
  onDeleteSensorModalStateChange: (isOpen: boolean) => void;
  deleteDeviceAndSensorFromGroup: (
    deviceGroupId: number,
    deviceNumber: string,
    sensorIndex: number
  ) => void;
}

export default SensorsTable;
